import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import { TIPO_META, URGENCIA_META } from "@/lib/notifMeta";

/**
 * Ayuda de Alertas (notificaciones). Antes, el «?» con globito y los
 * tooltips de «Marcar todas como leídas» y del ✓ de cada alerta.
 */

// Qué es cada tipo. El ícono y el nombre salen de TIPO_META (los mismos que
// pinta la lista); si agregas un tipo allá, explícalo aquí.
const QUE_ES: Record<string, string> = {
  mencion: "Un compañero te mencionó en la bitácora de un caso.",
  escalacion: "Un caso lleva tantos días sin movimiento que ya pasó de quemado y se escaló a la administración.",
  quemado: "Un caso pasó el límite de días sin movimiento.",
  por_quemarse: "Un caso está cerca del límite de días sin movimiento.",
  recordatorio: "Llegó la hora de un recordatorio puesto en un caso.",
  asignacion: "Te asignaron un caso.",
  nuevo_correo: "Llegó un correo nuevo a un caso.",
  buzon_pendiente: "En tu buzón hay correo que parece de soporte y todavía no está en ningún caso.",
  cierre: "Se cerró un caso que tú abriste.",
  sistema: "Avisos del sistema.",
};

const TIPOS: ItemAyuda[] = Object.entries(TIPO_META).map(([tipo, meta]) => ({
  label: (
    <span className={`inline-flex items-center gap-1 text-xs ${meta.color}`}>
      <span aria-hidden>{meta.icono}</span>
      {meta.label}
    </span>
  ),
  text: QUE_ES[tipo] ?? "",
}));

export function ayudaAlertas(): ContenidoAyuda {
  return {
    manual: "notificaciones",
    intro: (
      <p>
        Todos tus avisos, agrupados por urgencia. Aquí siempre llega todo; por dónde te llegan
        además (avisos del navegador y Telegram) se ajusta en Mi perfil → Notificaciones. Se
        muestran las 50 más recientes.
      </p>
    ),
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Tocar una alerta",
            text: "Te lleva al caso y la deja leída. Si la alerta no es de un caso, solo la marca como leída.",
          },
          { label: "✓", text: "La marca como leída sin abrir el caso." },
          {
            label: "Marcar todas (en un grupo)",
            text: "Marca como leídas las alertas sin leer que ves en ese grupo, con los filtros que tengas puestos.",
          },
          {
            label: "Marcar todas como leídas",
            text: "Marca como leídas TODAS tus alertas, aunque tengas filtros puestos. Sale gris si no tienes ninguna sin leer.",
          },
          { label: "Limpiar filtros", text: "Quita «Solo míos», «No leídas» y los tipos elegidos." },
        ],
      },
      {
        titulo: "Filtros",
        items: [
          {
            label: "Solo míos",
            text: "Tus menciones, tus recordatorios y las alertas de casos asignados a ti.",
          },
          { label: "No leídas", text: "Esconde las que ya leíste." },
          {
            label: "Fichas de tipo",
            text: "Toca una o varias para ver solo esos tipos; tócala otra vez para quitarla.",
          },
        ],
      },
      {
        titulo: "Qué significa cada grupo",
        items: [
          {
            label: URGENCIA_META.urgente.titulo,
            text: "Pide acción pronto. De fábrica: menciones, escalaciones, quemados y recordatorios.",
          },
          {
            label: URGENCIA_META.normal.titulo,
            text: "Hay que verlo, pero no es urgente. De fábrica: asignaciones, correos nuevos, buzón y por quemarse.",
          },
          {
            label: URGENCIA_META.informativa.titulo,
            text: "Solo para que sepas. De fábrica: cierres y avisos del sistema.",
          },
          {
            label: "Cambiar un tipo de grupo",
            text: "Se hace en Mi perfil → Notificaciones → Urgencia por tipo; mueve la alerta de grupo aquí y cambia por dónde te llega.",
          },
          { label: "Número junto al grupo", text: "Cuántas sin leer hay en ese grupo." },
          { label: "Renglón en gris · Leída", text: "Ya la leíste." },
        ],
      },
      { titulo: "Qué significa cada tipo", items: TIPOS },
    ],
  };
}
